"use client";
import Image from "next/image";
import { Image as HeroImage, Button } from "@heroui/react";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { getCookie } from "@/lib/helper";
import MenuButton from "@/components/MenuButton";
import { LogoutButton } from "@/components/LogoutButton";
import { motion } from "framer-motion";
import { useModal } from "./modal-context";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import LanguageButton from "@/components/language-button";
import { FaTelegram, FaFacebook, FaYoutube } from "react-icons/fa";
import { GetAvailableGameCategory } from "@/lib/actions";
import _ from "lodash";
export default function NavBar({ locale, isLoggedIn }: { locale: string; isLoggedIn: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} isLoggedIn={isLoggedIn} />
      <nav
        id="nav-top"
        className="flex flex-row items-center justify-between bg-gradient-to-b from-[#014235] to-[#02292B] px-4 py-5 shadow-lg"
      >
        <motion.button
          animate={{
            opacity: isOpen ? 0 : 1,
          }}
          onClick={() => setIsOpen(true)}
          className="icon-icon1 text-[1.5rem] text-[#E1E1E1] sm:text-[2rem]"
        />
        <Link href={"/"} className="aspect-[500/130] h-10">
          <HeroImage src="/maha_logo1.webp" className="-mt-1 object-contain" alt="maha6 logo" radius="none" />
        </Link>
        <LanguageButton locale={locale} />
      </nav>
    </>
  );
}

const MenuTitle = ({ children }: { children: ReactNode }) => (
  <div className="bg-gradient-to-r from-[rgb(255,234,146)] via-[#C9A04C] to-[#FFFFA5] bg-clip-text pl-2 text-xl font-bold text-transparent">
    {children}
  </div>
);

function Sidebar({
  isOpen,
  setIsOpen,
  isLoggedIn,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoggedIn: boolean;
}) {
  const t = useTranslations();

  const defaultGameItems = [
    { icon: "recents", tag: "recents", label: t("recents"), link: "/games/by-category/recents" },
    { icon: "icon-icon31", tag: "hot_games", label: t("hot_games"), link: "/games/by-category/hot_games" },
    { icon: "icon-icon18", tag: "slot", label: t("slot-games"), link: "/games/by-category/slot" },
    { icon: "icon-icon19", tag: "cricket", label: t("cricket-games"), link: "/games/by-category/cricket" },
    { icon: "icon-icon20", tag: "live", label: t("live"), link: "/games/by-category/live" },
    { icon: "icon-icon22", tag: "table_games", label: t("table-games"), link: "/games/by-category/table" },
    { icon: "icon-icon23", tag: "sports", label: t("sportsbook"), link: "/games/by-category/sports" },
    { icon: "icon-icon24", tag: "fishing", label: t("fishing"), link: "/games/by-category/fishing" },
    { icon: "icon-icon25", tag: "crash", label: t("crash"), link: "/games/by-category/crash" },
  ];

  // const gameItems = useMemo(() => {
  //   let gameItems = defaultGameItems;
  //   GetAvailableGameCategory()
  //     .then((res) => {
  //       const gi = _.filter(defaultGameItems, (item) => {
  //         return _.includes(res, item.tag);
  //       });
  //       gameItems = gi;
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //       gameItems = defaultGameItems;
  //     });
  //   return gameItems;
  // }, []);

  const [gameItems, setGameItems] = useState(defaultGameItems);

  useEffect(() => {
    isLoggedIn &&
      GetAvailableGameCategory()
        .then((res) => {
          // console.log("Game Category: ", res);
          if (_.isEmpty(res)) return;
          const gi = _.filter(defaultGameItems, (item) => item.tag === "recents" || _.includes(res, item.tag));
          setGameItems(gi);
        })
        .catch((err) => {
          console.log(err);
          setGameItems(defaultGameItems);
        });
  }, [isLoggedIn]);

  const quickLinks = [
    { icon: "icon-icon56", label: t("deposit"), link: "/profile/deposit" },
    // { icon: "icon-icon31", label: t("promotion"), link: "/promotion" },
    { icon: "promotion", label: t("promotion"), link: "/promotion" },
    { icon: "icon-icon32", label: t("vip-privilege"), link: "/vip" },
    // { icon: "icon-icon30", label: "Winning List", link: "coming soon" },
  ];

  const socialMedia = [
    {
      icon: <FaTelegram size={24} />,
      link: "https://t.me/join_maha6_now",
      label: "Telegram",
    },
    {
      icon: <FaFacebook size={24} />,
      link: "https://www.facebook.com/vangadhew55?mibextid=ZbWKwL",
      label: "Facebook",
    },
    {
      icon: <FaYoutube size={24} />,
      link: "https://www.youtube.com/channel/UCAyfl8GhM6dAMjkFa6A9pvw",
      label: "Youtube",
    },
  ];

  return (
    <>
      <div
        role="button"
        className={clsx(
          `absolute left-0 top-0 z-[1000] flex h-svh w-full flex-row-reverse justify-between overflow-hidden bg-opacity-50 duration-300 ease-in-out`,
          isOpen ? "pointer-events-auto bg-black" : "pointer-events-none bg-transparent"
        )}
        onClick={() => {
          setIsOpen(false);
        }}
      >
        <div
          className={`icon-icon17 absolute left-0 top-0 flex size-20 items-center justify-center text-[1.5rem] duration-300 ease-out md:text-[2rem] ${isOpen ? "opacity-100 hover:cursor-pointer" : "opacity-0"} `}
        />
        <div
          className={`flex h-auto w-9/12 flex-col bg-gradient-to-b from-[#021517] to-[#0A2A27] shadow-lg duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="w-full bg-gradient-to-b from-[#014235] to-[#02292B] py-6">
            <Image
              src="/maha_logo1.webp"
              priority
              className="mx-auto -mt-1 w-32"
              alt="maha6 logo"
              width={500}
              height={130}
            />
          </div>
          <div className="space-y-2 overflow-y-auto p-4">
            <MenuTitle>{t("games")}</MenuTitle>
            {gameItems.map((item, index) => (
              <MenuButton key={index} icon={item.icon} text={item.label} link={item.link} setIsOpen={setIsOpen} />
            ))}
            <MenuTitle>{t("quick-links")}</MenuTitle>
            {quickLinks.map((item, index) => (
              <MenuButton key={index} icon={item.icon} text={item.label} link={item.link} setIsOpen={setIsOpen} />
            ))}
            <MenuTitle>{t("social-media")}</MenuTitle>
            <div className="space-x-2">
              {socialMedia.map((item, index) => (
                <Button
                  as={"a"}
                  href={item.link}
                  target="_blank"
                  key={index}
                  startContent={item.icon}
                  variant={"light"}
                  isIconOnly
                ></Button>
              ))}
            </div>
            {isLoggedIn && (
              <div className="flex w-full justify-center">
                <LogoutButton setIsOpen={setIsOpen} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
